import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable } from 'rxjs';

export type SupportedLanguage = 'de' | 'en';

@Injectable({
  providedIn: 'root',
})
export class LanguageService {
  private readonly STORAGE_KEY = 'tremaze-language';
  private readonly supportedLanguages: SupportedLanguage[] = ['de', 'en'];
  private currentLanguageSubject: BehaviorSubject<SupportedLanguage>;

  constructor(private translateService: TranslateService) {
    // Initialisiere die Sprache aus dem LocalStorage oder verwende Default
    const savedLanguage = this.getSavedLanguage();
    this.currentLanguageSubject = new BehaviorSubject<SupportedLanguage>(
      savedLanguage
    );

    // Setze verfügbare Sprachen
    this.translateService.addLangs(this.supportedLanguages);

    // Setze die initiale Sprache
    this.setLanguage(savedLanguage, false);
  }

  /**
   * Gibt die aktuell aktive Sprache als Observable zurück
   */
  get currentLanguage$(): Observable<SupportedLanguage> {
    return this.currentLanguageSubject.asObservable();
  }

  /**
   * Gibt die aktuell aktive Sprache zurück
   */
  get currentLanguage(): SupportedLanguage {
    return this.currentLanguageSubject.value;
  }

  /**
   * Gibt alle verfügbaren Sprachen zurück
   */
  get availableLanguages(): SupportedLanguage[] {
    return this.supportedLanguages;
  }

  /**
   * Setzt die aktive Sprache
   * @param language Die zu setzende Sprache
   * @param persist Ob die Sprache im LocalStorage gespeichert werden soll (default: true)
   */
  setLanguage(language: SupportedLanguage, persist = true): void {
    if (!this.supportedLanguages.includes(language)) {
      console.warn(
        `Language "${language}" is not supported. Falling back to "de".`
      );
      language = 'de';
    }

    this.translateService.use(language);
    this.currentLanguageSubject.next(language);

    if (persist) {
      this.saveLanguage(language);
    }
  }

  /**
   * Wechselt zur nächsten verfügbaren Sprache
   */
  toggleLanguage(): void {
    const currentIndex = this.supportedLanguages.indexOf(this.currentLanguage);
    const nextIndex = (currentIndex + 1) % this.supportedLanguages.length;
    this.setLanguage(this.supportedLanguages[nextIndex]);
  }

  /**
   * Versucht die Browser-Sprache zu setzen, falls unterstützt
   */
  useBrowserLanguage(): void {
    const browserLang = this.translateService.getBrowserLang();
    const supportedLang = this.supportedLanguages.find(
      (lang) => lang === browserLang
    );

    if (supportedLang) {
      this.setLanguage(supportedLang);
    } else {
      console.info(
        `Browser language "${browserLang}" not supported. Using default "de".`
      );
      this.setLanguage('de');
    }
  }

  /**
   * Speichert die Sprache im LocalStorage
   */
  private saveLanguage(language: SupportedLanguage): void {
    if (typeof localStorage === 'undefined') {
      return; // SSR - kein LocalStorage verfügbar
    }
    try {
      localStorage.setItem(this.STORAGE_KEY, language);
    } catch (error) {
      console.warn('Failed to save language to localStorage:', error);
    }
  }

  /**
   * Lädt die gespeicherte Sprache aus dem LocalStorage
   */
  private getSavedLanguage(): SupportedLanguage {
    if (typeof localStorage === 'undefined') {
      // SSR - kein LocalStorage, verwende Default
      return 'de';
    }

    try {
      const saved = localStorage.getItem(this.STORAGE_KEY) as SupportedLanguage;
      if (saved && this.supportedLanguages.includes(saved)) {
        return saved;
      }
    } catch (error) {
      console.warn('Failed to load language from localStorage:', error);
    }

    // Fallback auf Browser-Sprache oder Deutsch
    const browserLang = this.translateService.getBrowserLang();
    return this.supportedLanguages.includes(browserLang as SupportedLanguage)
      ? (browserLang as SupportedLanguage)
      : 'de';
  }

  /**
   * Übersetzt einen Key sofort (nicht als Observable)
   * @param key Der Übersetzungs-Key
   * @param params Optionale Parameter für die Interpolation
   */
  instant(key: string, params?: any): string {
    return this.translateService.instant(key, params);
  }

  /**
   * Übersetzt einen Key als Observable
   * @param key Der Übersetzungs-Key
   * @param params Optionale Parameter für die Interpolation
   */
  get(key: string, params?: any): Observable<string> {
    return this.translateService.get(key, params);
  }
}
